// src/components/exercises/PlankCamera.js
import React, { useState, useRef, useEffect, useCallback } from 'react';
import { analyzePlank, drawPlankSkeleton } from './plankDetection';
import { db } from '../../services/firebase/config';
import { doc, setDoc, getDoc, serverTimestamp } from 'firebase/firestore';
import './PlankCamera.css';

const HOLD_TO_START = 3;
const PAUSE_MAX = 3;
const COUNTDOWN_FROM = 10;

const PlankCamera = ({ exercise, onComplete, onBack, userId, clapUrl }) => {
  const language = localStorage.getItem('spocademy_language') || 'mr';

  const TARGET = exercise?.reps || 15;       // seconds per set
  const TOTAL_SETS = exercise?.sets || 2;
  const REST = exercise?.restTime || 5;

  const T = React.useMemo(() => (language === 'mr' ? {
    title: 'प्लँक', sec: 'सेकंद', set: 'सेट', rest: 'विश्रांती',
    getPos: 'position मध्ये या', hold: 'धरून ठेव', good: 'छान!',
    tilt: '1. फोन आडवा फिरवा', tiltSub: '2. फोन चा आवाज वाढवा',
    formBroke: 'form तुटला - पुन्हा सुरू करा',
    sag: 'कंबर वर', pike: 'कंबर खाली', knee: 'गुडघे सरळ',
    shoulder: 'कोपर खांद्याखाली', vis: 'पूर्ण शरीर दाखवा', flat: 'सरळ पडून राहा',
    lift: 'खांदे वर उचला', start: 'सुरू', done: 'सेकंद पूर्ण!', setReady: 'सेट',
    confirmClose: 'बंद केल्यास प्रगती जाईल. खात्री आहे का?', yes: 'होय', no: 'नाही'
  } : {
    title: 'Plank', sec: 'seconds', set: 'SET', rest: 'REST',
    getPos: 'Get into position', hold: 'Hold', good: 'Good!',
    tilt: '1. Rotate your phone', tiltSub: '2. Increase phone volume',
    formBroke: 'Form broke - start again',
    sag: 'Hips up', pike: 'Hips down', knee: 'Legs straight',
    shoulder: 'Elbows under shoulders', vis: 'Show full body', flat: 'Lie flat',
    lift: 'Lift your shoulders up', start: 'Start', done: 'seconds done!', setReady: 'Set',
    confirmClose: 'Closing will lose your progress. Are you sure?', yes: 'Yes', no: 'No'
  }), [language]);

  const [showConfirmClose, setShowConfirmClose] = useState(false);
  const [showOutline, setShowOutline] = useState(true);
  const [showCompletion, setShowCompletion] = useState(false);
  const [totalLabel, setTotalLabel] = useState('');

  const videoRef = useRef();
  const canvasRef = useRef();
  const cameraWrapRef = useRef();
  const cameraInnerRef = useRef();
  const poseRef = useRef();
  const cameraRef = useRef();
  const wakeLockRef = useRef(null);
  const running = useRef(false);

  const audioCtxRef = useRef(null);
  const completionTimerRef = useRef(null);

  // uploaded audio (Firestore) — same source squats/JJ use
  const exerciseCompleteAudioRef = useRef(null);
  const restStartAudioRef = useRef(null);

  // timer/state — refs, not React state, since they update every frame
  const state = useRef('WAITING');
  const elapsed = useRef(0);
  const banked = useRef(0);
  const currentSet = useRef(1);
  const holdStart = useRef(null);
  const pauseStart = useRef(null);
  const restEnd = useRef(null);
  const lastBeepSec = useRef(-1);
  const lastRestSpoken = useRef(-1);
  const recent = useRef([]);
  const currentFail = useRef(null);
  const smoothScore = useRef(0);
  const lastFrameTime = useRef(null);

  // rotation
  const gravityHW = useRef(null);
  const hasMotion = useRef(false);
  const isPhoneHorizontal = useRef(false);
  const physicalTheta = useRef(0);
  const currentRot = useRef(null);
  const stageRef = useRef();

  const barFillRef = useRef();
  const progressFillRef = useRef();
  const statusRef = useRef();
  const stripMidRef = useRef();
  const rotateMsgRef = useRef();

  // ---- preload uploaded audio (mirrors EnhancedCamera exactly) ----
  useEffect(() => {
    const preloadAudio = async () => {
      try {
        const [exerciseSnap, restSnap] = await Promise.all([
          getDoc(doc(db, 'eventSounds', 'exercise_complete')),
          getDoc(doc(db, 'eventSounds', 'rest_start'))
        ]);
        if (exerciseSnap.exists() && exerciseSnap.data().audioURL) {
          const a = new Audio(exerciseSnap.data().audioURL);
          a.preload = 'auto';
          exerciseCompleteAudioRef.current = a;
        }
        if (restSnap.exists() && restSnap.data().audioURL) {
          const a = new Audio(restSnap.data().audioURL);
          a.preload = 'auto';
          restStartAudioRef.current = a;
        }
      } catch (e) {}
    };
    preloadAudio();
  }, []);

  // ---- audio ----
  const initAudio = useCallback(() => {
    if (!audioCtxRef.current) {
      try { audioCtxRef.current = new (window.AudioContext || window.webkitAudioContext)(); } catch (e) {}
    }
    if (audioCtxRef.current?.state === 'suspended') audioCtxRef.current.resume().catch(() => {});
  }, []);

  const tone = useCallback((freq, dur, vol) => {
    initAudio();
    const ctx = audioCtxRef.current;
    if (!ctx) return;
    try {
      const o = ctx.createOscillator(), g = ctx.createGain();
      o.connect(g); g.connect(ctx.destination);
      o.frequency.setValueAtTime(freq, ctx.currentTime);
      g.gain.setValueAtTime(vol, ctx.currentTime);
      g.gain.exponentialRampToValueAtTime(0.01, ctx.currentTime + dur);
      o.start(ctx.currentTime); o.stop(ctx.currentTime + dur);
    } catch (e) {}
  }, [initAudio]);

  const beep = useCallback(() => tone(800, 0.16, 0.7), [tone]);
  const resumeTone = useCallback(() => { tone(600, 0.1, 0.3); setTimeout(() => tone(900, 0.12, 0.3), 100); }, [tone]);

  // whistle: try uploaded rest_start audio first, else synth (matches EnhancedCamera)
  const whistle = useCallback(() => {
    if (restStartAudioRef.current) {
      try {
        restStartAudioRef.current.currentTime = 0;
        restStartAudioRef.current.play().catch(() => {
          initAudio();
          const ctx = audioCtxRef.current;
          if (!ctx) return;
          try {
            const o = ctx.createOscillator(), g = ctx.createGain();
            o.connect(g); g.connect(ctx.destination);
            o.frequency.setValueAtTime(2000, ctx.currentTime);
            o.frequency.exponentialRampToValueAtTime(2500, ctx.currentTime + 0.1);
            o.frequency.exponentialRampToValueAtTime(2000, ctx.currentTime + 0.2);
            g.gain.setValueAtTime(0.4, ctx.currentTime);
            g.gain.exponentialRampToValueAtTime(0.01, ctx.currentTime + 0.3);
            o.start(ctx.currentTime); o.stop(ctx.currentTime + 0.3);
          } catch (e) {}
        });
        return;
      } catch (e) {}
    }
    initAudio();
    const ctx = audioCtxRef.current;
    if (!ctx) return;
    try {
      const o = ctx.createOscillator(), g = ctx.createGain();
      o.connect(g); g.connect(ctx.destination);
      o.frequency.setValueAtTime(2000, ctx.currentTime);
      o.frequency.exponentialRampToValueAtTime(2500, ctx.currentTime + 0.1);
      o.frequency.exponentialRampToValueAtTime(2000, ctx.currentTime + 0.2);
      g.gain.setValueAtTime(0.4, ctx.currentTime);
      g.gain.exponentialRampToValueAtTime(0.01, ctx.currentTime + 0.3);
      o.start(ctx.currentTime); o.stop(ctx.currentTime + 0.3);
    } catch (e) {}
  }, [initAudio]);

  // playClap: identical priority/fallback chain to EnhancedCamera.js
  const playClap = useCallback(() => {
    if (clapUrl) {
      try { const a = new Audio(clapUrl); a.play().catch(() => {}); return; } catch (e) {}
    }
    if (exerciseCompleteAudioRef.current) {
      try {
        exerciseCompleteAudioRef.current.currentTime = 0;
        exerciseCompleteAudioRef.current.play().catch(() => {
          initAudio();
          const ctx = audioCtxRef.current;
          if (!ctx) return;
          const burst = (time) => {
            try {
              const n = Math.floor(ctx.sampleRate * 0.15);
              const buf = ctx.createBuffer(1, n, ctx.sampleRate);
              const d = buf.getChannelData(0);
              for (let i = 0; i < n; i++) d[i] = (Math.random() * 2 - 1) * Math.exp(-i / (ctx.sampleRate * 0.03));
              const s = ctx.createBufferSource(); s.buffer = buf;
              const f = ctx.createBiquadFilter(); f.type = 'bandpass'; f.frequency.value = 1200; f.Q.value = 0.8;
              const g = ctx.createGain();
              g.gain.setValueAtTime(0.6, time);
              g.gain.exponentialRampToValueAtTime(0.01, time + 0.12);
              s.connect(f); f.connect(g); g.connect(ctx.destination); s.start(time);
            } catch (e) {}
          };
          burst(ctx.currentTime); burst(ctx.currentTime + 0.12); burst(ctx.currentTime + 0.22);
        });
        return;
      } catch (e) {}
    }
    initAudio();
    const ctx = audioCtxRef.current;
    if (!ctx) return;
    const burst = (time) => {
      try {
        const n = Math.floor(ctx.sampleRate * 0.15);
        const buf = ctx.createBuffer(1, n, ctx.sampleRate);
        const d = buf.getChannelData(0);
        for (let i = 0; i < n; i++) d[i] = (Math.random() * 2 - 1) * Math.exp(-i / (ctx.sampleRate * 0.03));
        const s = ctx.createBufferSource(); s.buffer = buf;
        const f = ctx.createBiquadFilter(); f.type = 'bandpass'; f.frequency.value = 1200; f.Q.value = 0.8;
        const g = ctx.createGain();
        g.gain.setValueAtTime(0.6, time);
        g.gain.exponentialRampToValueAtTime(0.01, time + 0.12);
        s.connect(f); f.connect(g); g.connect(ctx.destination); s.start(time);
      } catch (e) {}
    };
    burst(ctx.currentTime); burst(ctx.currentTime + 0.12); burst(ctx.currentTime + 0.22);
  }, [initAudio, clapUrl]);

  const speak = useCallback((txt) => {
    if (!window.speechSynthesis) return;
    window.speechSynthesis.cancel();
    const u = new SpeechSynthesisUtterance(txt);
    u.lang = language === 'mr' ? 'hi-IN' : 'en-US';
    u.rate = 1.0; u.volume = 0.9;
    window.speechSynthesis.speak(u);
  }, [language]);

  // ---- repeating spoken correction while paused ----
  // speaks the current fail reason, waits for it to finish + 250ms, repeats.
  // stops the instant form is fixed or pause ends.
  const speakRepeatTimeout = useRef(null);
  const speakRepeatActive = useRef(false);

  const speakWithCallback = useCallback((txt, onDone) => {
    if (!window.speechSynthesis) { onDone(); return; }
    window.speechSynthesis.cancel();
    const u = new SpeechSynthesisUtterance(txt);
    u.lang = language === 'mr' ? 'hi-IN' : 'en-US';
    u.rate = 1.0; u.volume = 0.9;
    u.onend = onDone;
    u.onerror = onDone;
    window.speechSynthesis.speak(u);
  }, [language]);

  const startPauseRepeat = useCallback(() => {
    speakRepeatActive.current = true;
    const loop = () => {
      if (!speakRepeatActive.current) return;
      const msg = currentFail.current ? T[currentFail.current] : T.formBroke;
      speakWithCallback(msg, () => {
        if (!speakRepeatActive.current) return;
        speakRepeatTimeout.current = setTimeout(loop, 250);
      });
    };
    loop();
  }, [speakWithCallback, T]);

  const stopPauseRepeat = useCallback(() => {
    speakRepeatActive.current = false;
    clearTimeout(speakRepeatTimeout.current);
    window.speechSynthesis?.cancel();
  }, []);

  // ---- wake lock ----
  const requestWakeLock = useCallback(async () => {
    try { wakeLockRef.current = await navigator.wakeLock.request('screen'); } catch (e) {}
  }, []);
  useEffect(() => {
    const onVis = () => { if (document.visibilityState === 'visible' && running.current) requestWakeLock(); };
    document.addEventListener('visibilitychange', onVis);
    return () => document.removeEventListener('visibilitychange', onVis);
  }, [requestWakeLock]);

  // ---- rotation ----
  const screenAngle = () => {
    if (window.screen.orientation && typeof window.screen.orientation.angle === 'number') return window.screen.orientation.angle;
    if (typeof window.orientation === 'number') return window.orientation;
    return 0;
  };

  const applyLayout = useCallback(() => {
    if (!running.current || !stageRef.current) return;
    if (!hasMotion.current) {
      physicalTheta.current = screenAngle();
      if (physicalTheta.current === 270) physicalTheta.current = -90;
      isPhoneHorizontal.current = (physicalTheta.current === 90 || physicalTheta.current === -90);
    }
    let rot = physicalTheta.current - screenAngle();
    rot = ((rot % 360) + 360) % 360;
    if (rot === 180) rot = 0;
    if (rot === 270) rot = -90;
    if (rotateMsgRef.current) rotateMsgRef.current.style.display = isPhoneHorizontal.current ? 'none' : 'flex';
    if (rot === currentRot.current) return;
    currentRot.current = rot;
    const W = window.innerWidth, H = window.innerHeight;
    const stage = stageRef.current;
    if (rot === 0) {
      stage.style.width = W + 'px'; stage.style.height = H + 'px';
      stage.style.transform = 'translate(-50%,-50%)';
    } else {
      stage.style.width = H + 'px'; stage.style.height = W + 'px';
      stage.style.transform = `translate(-50%,-50%) rotate(${rot}deg)`;
    }
    const wrap = cameraWrapRef.current;
    const inner = cameraInnerRef.current;
    if (wrap && inner) {
      requestAnimationFrame(() => {
        const wrapW = wrap.clientWidth, wrapH = wrap.clientHeight;
        if (rot === 0) {
          inner.style.width = wrapW + 'px';
          inner.style.height = wrapH + 'px';
          inner.style.transform = 'translate(-50%,-50%) rotate(0deg)';
        } else {
          inner.style.width = wrapH + 'px';
          inner.style.height = wrapW + 'px';
          inner.style.transform = `translate(-50%,-50%) rotate(${-rot}deg)`;
        }
      });
    }
  }, []);

  useEffect(() => {
    const onMotion = (e) => {
      const g = e.accelerationIncludingGravity;
      if (!g || g.x === null) return;
      hasMotion.current = true;
      gravityHW.current = { x: g.x, y: g.y };
      const mag = Math.hypot(g.x, g.y);
      if (mag < 2) return;
      const ax = Math.abs(g.x), ay = Math.abs(g.y);
      if (ax > ay * 1.2) physicalTheta.current = (g.x > 0) ? 90 : -90;
      else if (ay > ax * 1.2) physicalTheta.current = (g.y > 0) ? 0 : 180;
      isPhoneHorizontal.current = (physicalTheta.current === 90 || physicalTheta.current === -90);
      applyLayout();
    };
    window.addEventListener('devicemotion', onMotion);
    const onResize = () => { currentRot.current = null; applyLayout(); };
    window.addEventListener('resize', onResize);
    if (window.screen.orientation) window.screen.orientation.addEventListener('change', onResize);
    return () => {
      window.removeEventListener('devicemotion', onMotion);
      window.removeEventListener('resize', onResize);
      if (window.screen.orientation) window.screen.orientation.removeEventListener('change', onResize);
    };
  }, [applyLayout]);

  const gravityAxisRaw = () => {
    if (!gravityHW.current) return null;
    const mag = Math.hypot(gravityHW.current.x, gravityHW.current.y);
    if (mag < 2) return null;
    let hw = Math.atan2(gravityHW.current.y, gravityHW.current.x) * 180 / Math.PI;
    return ((hw % 180) + 180) % 180;
  };

  const computeCoverRect = (srcW, srcH, dstW, dstH) => {
    const srcAspect = srcW / srcH, dstAspect = dstW / dstH;
    let sx = 0, sy = 0, sw = srcW, sh = srcH;
    if (srcAspect > dstAspect) { sw = srcH * dstAspect; sx = (srcW - sw) / 2; }
    else { sh = srcW / dstAspect; sy = (srcH - sh) / 2; }
    return { sx, sy, sw, sh };
  };

  // ---- UI render (direct DOM, matches test-page approach for perf) ----
  const renderUI = useCallback(() => {
    const st = state.current;
    if (stripMidRef.current) {
      if (st === 'REST') {
        const left = Math.max(0, Math.ceil((restEnd.current - Date.now()) / 1000));
        stripMidRef.current.innerHTML =
          `<span class="pc-strip-title">${T.title}</span><span class="pc-strip-rest">${T.rest} ${left}</span>`;
      } else {
        const remaining = Math.max(0, Math.ceil(TARGET - elapsed.current));
        const cd = (st === 'RUNNING' && (TARGET - elapsed.current) <= COUNTDOWN_FROM) ? ' countdown' : '';
        stripMidRef.current.innerHTML =
          `<span class="pc-strip-title">${T.title}</span>` +
          `<span class="pc-strip-timer${cd}">${remaining}<span class="pc-strip-unit"> ${T.sec}</span></span>` +
          `<span><span class="pc-strip-set">${T.set} </span><span class="pc-strip-setnum">${currentSet.current}/${TOTAL_SETS}</span></span>`;
      }
    }
    if (progressFillRef.current) {
      if (st === 'REST') {
        const done = 1 - Math.max(0, (restEnd.current - Date.now()) / 1000) / REST;
        progressFillRef.current.className = 'pc-progress-fill rest';
        progressFillRef.current.style.width = (done * 100) + '%';
      } else {
        progressFillRef.current.className = 'pc-progress-fill';
        progressFillRef.current.style.width = ((elapsed.current / TARGET) * 100) + '%';
      }
    }
    if (statusRef.current) {
      let msg = '';
      if (st === 'WAITING') {
        if (holdStart.current) msg = T.hold + ' ' + Math.ceil(HOLD_TO_START - (Date.now() - holdStart.current) / 1000);
        else msg = currentFail.current ? T[currentFail.current] : T.getPos;
      } else if (st === 'RUNNING') msg = T.good;
      else if (st === 'PAUSED') {
        const left = Math.max(0, Math.ceil(PAUSE_MAX - (Date.now() - pauseStart.current) / 1000));
        msg = (currentFail.current ? T[currentFail.current] : '') + ' (' + left + ')';
      } else if (st === 'PAUSED_VIS') msg = T.vis;
      else if (st === 'REST') msg = T.setReady + ' ' + currentSet.current;
      statusRef.current.textContent = msg;
    }
  }, [T, TARGET, TOTAL_SETS, REST]);

  // ---- completion / stats ----
  const updateUserStatsBackground = useCallback(async (totalSeconds) => {
    if (!userId) return;
    try {
      const statsRef = doc(db, 'fitnessUserStats', userId);
      const snap = await getDoc(statsRef);
      const existing = snap.exists() ? snap.data() : {};
      await setDoc(statsRef, {
        ...existing,
        totalPlankSeconds: (existing.totalPlankSeconds || 0) + totalSeconds,
        updatedAt: serverTimestamp()
      });
    } catch (e) {}
  }, [userId]);

  const finishExercise = useCallback(() => {
    running.current = false;
    const total = banked.current;
    const name = typeof exercise?.name === 'object' ? (exercise.name[language] || exercise.name.en) : (exercise?.name || 'Plank');
    setTotalLabel(`${total} ${T.sec} ${name} ${T.done}`);
    updateUserStatsBackground(total);
    playClap();
    if (navigator.vibrate) navigator.vibrate([100, 50, 100, 50, 200]);
    completionTimerRef.current = setTimeout(() => setShowCompletion(true), 500);
  }, [exercise, language, T, updateUserStatsBackground, playClap]);

  // ---- timer step (called every frame) ----
  const stepTimer = useCallback((fullPlank, bodySeen, dt) => {
    const now = Date.now();
    if (state.current === 'DONE') return;

    if (state.current === 'REST') {
      const left = Math.ceil((restEnd.current - now) / 1000);
      if (left <= 5 && left > 0 && left !== lastRestSpoken.current) { lastRestSpoken.current = left; speak(String(left)); }
      if (now >= restEnd.current) {
        state.current = 'WAITING'; holdStart.current = null; lastRestSpoken.current = -1; whistle();
      }
      return;
    }

    if (!bodySeen && state.current !== 'PAUSED') {
      if (state.current === 'RUNNING') { state.current = 'PAUSED_VIS'; startPauseRepeat(); }
      if (state.current === 'WAITING') holdStart.current = null;
      return;
    }

    if (state.current === 'WAITING') {
      if (fullPlank && isPhoneHorizontal.current) {
        if (!holdStart.current) holdStart.current = now;
        if ((now - holdStart.current) / 1000 >= HOLD_TO_START) {
          state.current = 'RUNNING'; lastBeepSec.current = -1; speak(T.start); whistle();
        }
      } else holdStart.current = null;

    } else if (state.current === 'RUNNING') {
      elapsed.current += dt;
      const remaining = TARGET - elapsed.current;
      const sec = Math.floor(elapsed.current);
      if (sec !== lastBeepSec.current) {
        lastBeepSec.current = sec;
        if (remaining <= COUNTDOWN_FROM) speak(String(Math.ceil(remaining)));
        else beep();
      }
      if (elapsed.current >= TARGET) {
        banked.current += TARGET; whistle();
        if (currentSet.current >= TOTAL_SETS) {
          state.current = 'DONE';
          finishExercise();
        } else {
          currentSet.current++; elapsed.current = 0; state.current = 'REST'; restEnd.current = now + REST * 1000;
        }
        return;
      }
      if (!fullPlank) { state.current = 'PAUSED'; pauseStart.current = now; startPauseRepeat(); }

    } else if (state.current === 'PAUSED') {
      if (bodySeen && fullPlank) { state.current = 'RUNNING'; pauseStart.current = null; lastBeepSec.current = -1; stopPauseRepeat(); resumeTone(); }
      else {
        if ((now - pauseStart.current) / 1000 >= PAUSE_MAX) {
          elapsed.current = 0; stopPauseRepeat();
          state.current = 'WAITING'; holdStart.current = null; pauseStart.current = null; speak(T.formBroke);
        }
      }
    } else if (state.current === 'PAUSED_VIS') {
      if (fullPlank) { state.current = 'RUNNING'; lastBeepSec.current = -1; stopPauseRepeat(); resumeTone(); }
    }
  }, [T, TARGET, TOTAL_SETS, REST, speak, whistle, beep, startPauseRepeat, stopPauseRepeat, resumeTone, finishExercise]);

  // ---- frame handler ----
  const onResults = useCallback((results) => {
    if (!running.current) return;
    const video = videoRef.current, canvas = canvasRef.current, wrap = cameraInnerRef.current;
    if (!video || !canvas || !wrap) return;
    const ctx = canvas.getContext('2d');
    const vw = video.videoWidth || 640, vh = video.videoHeight || 480;
    const dstW = wrap.clientWidth || vw, dstH = wrap.clientHeight || vh;
    if (canvas.width !== dstW || canvas.height !== dstH) { canvas.width = dstW; canvas.height = dstH; }
    ctx.clearRect(0, 0, canvas.width, canvas.height);
    const crop = computeCoverRect(vw, vh, canvas.width, canvas.height);
    ctx.drawImage(video, crop.sx, crop.sy, crop.sw, crop.sh, 0, 0, canvas.width, canvas.height);
    const mapX = (nx) => ((nx * vw) - crop.sx) * (canvas.width / crop.sw);
    const mapY = (ny) => ((ny * vh) - crop.sy) * (canvas.height / crop.sh);

    const now = Date.now();
    const dt = lastFrameTime.current ? (now - lastFrameTime.current) / 1000 : 0;
    lastFrameTime.current = now;

    if (!results.poseLandmarks) {
      smoothScore.current *= 0.8;
      if (barFillRef.current) barFillRef.current.style.height = Math.round(smoothScore.current * 100) + '%';
      recent.current.push({ time: now, pass: false });
      recent.current = recent.current.filter(r => now - r.time <= 1000);
      currentFail.current = 'vis';
      stepTimer(false, false, dt); renderUI();
      return;
    }

    drawPlankSkeleton(ctx, results.poseLandmarks, mapX, mapY);
    const r = analyzePlank(results.poseLandmarks, gravityAxisRaw(), screenAngle());
    currentFail.current = r.currentFail;

    recent.current.push({ time: now, pass: r.fullPlankRaw });
    recent.current = recent.current.filter(rr => now - rr.time <= 1000);
    const fullPlank = recent.current.filter(rr => rr.pass).length > recent.current.length / 2;

    smoothScore.current = smoothScore.current * 0.7 + (r.score || 0) * 0.3;
    if (barFillRef.current) {
      barFillRef.current.style.height = Math.round(smoothScore.current * 100) + '%';
      barFillRef.current.style.background = fullPlank ? '#28a745' : '#7bd39a';
    }

    stepTimer(fullPlank, r.visOK, dt);
    renderUI();
  }, [stepTimer, renderUI]);

  // ---- camera start ----
  const startCamera = useCallback(async () => {
    try {
      const pose = new window.Pose({ locateFile: (f) => `https://cdn.jsdelivr.net/npm/@mediapipe/pose/${f}` });
      pose.setOptions({ modelComplexity: 0, smoothLandmarks: true, enableSegmentation: false, minDetectionConfidence: 0.5, minTrackingConfidence: 0.5 });
      pose.onResults(onResults);
      poseRef.current = pose;

      const stream = await navigator.mediaDevices.getUserMedia({ video: { width: { ideal: 640 }, height: { ideal: 480 }, facingMode: 'user' } });
      videoRef.current.srcObject = stream;

      await new Promise((resolve, reject) => {
        const timeout = setTimeout(() => reject(new Error('timeout')), 15000);
        const done = () => { clearTimeout(timeout); resolve(); };
        videoRef.current.addEventListener('loadeddata', done, { once: true });
        videoRef.current.addEventListener('canplay', done, { once: true });
        if (videoRef.current.readyState >= 2) done();
      });

      const camera = new window.Camera(videoRef.current, {
        onFrame: async () => { if (poseRef.current) { try { await poseRef.current.send({ image: videoRef.current }); } catch (e) {} } },
        width: 640, height: 480
      });
      cameraRef.current = camera;
      await camera.start();

      running.current = true;
      currentRot.current = null;
      requestWakeLock();
      applyLayout();
      renderUI();
    } catch (e) {
      if (statusRef.current) statusRef.current.textContent = language === 'mr' ? 'कॅमेरा त्रुटी' : 'Camera error';
    }
  }, [onResults, applyLayout, renderUI, requestWakeLock, language]);

  const cleanup = useCallback(() => {
    window.speechSynthesis?.cancel();
    stopPauseRepeat();
    clearTimeout(completionTimerRef.current);
    running.current = false;
    try { poseRef.current?.close(); } catch (e) {}
    poseRef.current = null;
    try { cameraRef.current?.stop(); } catch (e) {}
    cameraRef.current = null;
    if (videoRef.current?.srcObject) {
      videoRef.current.srcObject.getTracks().forEach(t => t.stop());
      videoRef.current.srcObject = null;
    }
    try { wakeLockRef.current?.release(); } catch (e) {}
    wakeLockRef.current = null;
  }, [stopPauseRepeat]);

  useEffect(() => {
    const outlineTimer = setTimeout(() => setShowOutline(false), 7000);
    let retries = 0;
    const tryStart = async () => {
      if (typeof window.Pose === 'undefined' || typeof window.Camera === 'undefined') {
        if (retries < 15) { retries++; setTimeout(tryStart, 2000); }
        return;
      }
      await startCamera();
    };
    setTimeout(tryStart, 500);
    return () => { clearTimeout(outlineTimer); cleanup(); };
  }, [startCamera, cleanup]); // eslint-disable-line react-hooks/exhaustive-deps

  useEffect(() => {
    if (!showCompletion) return;
    cleanup();
  }, [showCompletion, cleanup]);

  const handleConfirmClose = () => { cleanup(); if (onBack) onBack(); };
  const handleCompletionDone = () => { setShowCompletion(false); if (onComplete) onComplete(); };

  return (
    <div className="pc-container">
      <div ref={stageRef} className="pc-stage">
        <div className="pc-top-strip">
          <div style={{ width: 34 }} />
          <div className="pc-strip-mid" ref={stripMidRef} />
          <button className="pc-close-btn" onClick={() => setShowConfirmClose(true)}>✕</button>
        </div>

        <div className="pc-camera-wrap" ref={cameraWrapRef}>
          <div className="pc-camera-inner" ref={cameraInnerRef}>
            <video ref={videoRef} autoPlay muted playsInline className="pc-video" />
            <canvas ref={canvasRef} className="pc-canvas" />
            {showOutline && (
              <div className="pc-outline-wrap">
                <img src="/planks.svg" alt="" className="pc-outline-img" />
              </div>
            )}
            <div className="pc-bar-wrap"><div className="pc-bar-fill" ref={barFillRef} /></div>
          </div>
          <div className="pc-rotate-msg" ref={rotateMsgRef}>
            <div className="pc-rotate-card">
              <div className="pc-rotate-icon">📱↻</div>
              <div className="pc-rotate-txt">{T.tilt}</div>
              <div className="pc-rotate-subtxt">{T.tiltSub}</div>
            </div>
          </div>
        </div>

        <div className="pc-bottom-band">
          <div className="pc-status" ref={statusRef} />
          <div className="pc-progress-bar"><div className="pc-progress-fill" ref={progressFillRef} /></div>
        </div>
      </div>

      {showConfirmClose && (
        <div className="pc-overlay">
          <div className="pc-confirm-box">
            <p>{T.confirmClose}</p>
            <div className="pc-confirm-btns">
              <button className="pc-confirm-no" onClick={() => setShowConfirmClose(false)}>{T.no}</button>
              <button className="pc-confirm-yes" onClick={handleConfirmClose}>{T.yes}</button>
            </div>
          </div>
        </div>
      )}

      {showCompletion && (
        <div className="pc-overlay">
          <div className="pc-completion-box">
            <div className="pc-completion-emoji">💪</div>
            <div className="pc-completion-text">{totalLabel}</div>
            <button className="pc-confirm-yes" style={{ marginTop: 16 }} onClick={handleCompletionDone}>OK</button>
          </div>
        </div>
      )}
    </div>
  );
};

export default PlankCamera;